import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  fetchHistoricalTrackingAggregates,
  fetchCampaignScopedSnapshots,
} from "../services/outreach/engagementAnalyticsService";
import type {
  EnhancedMessageTrackingData,
  SesTrackingDailyAggregate,
} from "../services/outreach/types";
import {
  EngagementTimeRange,
  getDaysFromRange,
  getBouncedTimestamp,
  getClickedTimestamp,
  getComplaintTimestamp,
  getDeliveredTimestamp,
  getOpenedTimestamp,
  roundToTwo,
  toDateKey,
} from "../utils/engagementAnalytics";

export interface SESPerformanceTrendData {
  date: string;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  bounceRate: number;
  complaintRate: number;
}

const NO_DATA_PLACEHOLDER =
  "No engagement data for this campaign in selected range";
const DEBOUNCE_DELAY_MS = 200;

export interface SESPerformanceTrendResponse {
  dataset: SESPerformanceTrendData[];
  placeholderText: string | null;
  apiError: TrendDatasetApiError | null;
}

export type UseSESPerformanceTrendsResult = UseQueryResult<
  SESPerformanceTrendResponse
> & {
  dataset: SESPerformanceTrendData[];
  placeholderText: string | null;
  apiError: TrendDatasetApiError | null;
};

export interface TrendDatasetApiError {
  statusCode?: number;
  message: string;
}

const hasMeaningfulTrendData = (data: SESPerformanceTrendData[]): boolean =>
  data.some(
    (entry) =>
      entry.sent > 0 ||
      entry.delivered > 0 ||
      entry.opened > 0 ||
      entry.clicked > 0 ||
      entry.bounced > 0 ||
      entry.complained > 0
  );

const buildEmptyDayMap = (
  rangeStart: Date,
  days: number
): Map<string, SESPerformanceTrendData> => {
  const dayMap = new Map<string, SESPerformanceTrendData>();
  for (let i = 0; i < days; i += 1) {
    const day = new Date(rangeStart);
    day.setDate(rangeStart.getDate() + i);
    const key = toDateKey(day.getTime());
    dayMap.set(key, {
      date: key,
      sent: 0,
      delivered: 0,
      opened: 0,
      clicked: 0,
      bounced: 0,
      complained: 0,
      deliveryRate: 0,
      openRate: 0,
      clickRate: 0,
      bounceRate: 0,
      complaintRate: 0,
    });
  }
  return dayMap;
};

const mapAggregatesToDataset = (
  aggregates: SesTrackingDailyAggregate[],
  timeRange: EngagementTimeRange
): SESPerformanceTrendData[] => {
  const days = getDaysFromRange(timeRange);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const rangeStart = new Date(now);
  rangeStart.setDate(rangeStart.getDate() - (days - 1));
  const dayMap = buildEmptyDayMap(rangeStart, days);

  aggregates.forEach((aggregate) => {
    const dateKey = toDateKey(new Date(aggregate.date).getTime());
    const entry = dayMap.get(dateKey);
    if (!entry) {
      return;
    }

    entry.sent = aggregate.sent ?? 0;
    entry.delivered = aggregate.delivered ?? 0;
    entry.opened = aggregate.opened ?? 0;
    entry.clicked = aggregate.clicked ?? 0;
    entry.bounced = aggregate.bounced ?? 0;
    entry.complained = aggregate.complained ?? 0;
  });

  dayMap.forEach((entry) => {
    entry.deliveryRate =
      entry.sent > 0 ? roundToTwo((entry.delivered / entry.sent) * 100) : 0;
    entry.bounceRate =
      entry.sent > 0 ? roundToTwo((entry.bounced / entry.sent) * 100) : 0;
    entry.openRate =
      entry.delivered > 0
        ? roundToTwo((entry.opened / entry.delivered) * 100)
        : 0;
    entry.clickRate =
      entry.delivered > 0
        ? roundToTwo((entry.clicked / entry.delivered) * 100)
        : 0;
    entry.complaintRate =
      entry.delivered > 0
        ? roundToTwo((entry.complained / entry.delivered) * 100)
        : 0;
  });

  return Array.from(dayMap.values()).sort((a, b) =>
    a.date.localeCompare(b.date)
  );
};

const buildTrendDataset = async (
  timeRange: EngagementTimeRange,
  campaignId?: string
): Promise<SESPerformanceTrendResponse> => {
  let aggregateError: TrendDatasetApiError | null = null;
  let aggregated: SesTrackingDailyAggregate[] = [];

  try {
    aggregated = await fetchHistoricalTrackingAggregates(timeRange, campaignId);
    console.log(
      "aggregated data fetched for SES performance trends:",
      aggregated
    );
  } catch (error) {
    aggregateError = normalizeTrendApiError(error);
    console.error("SES performance trends aggregate fetch failed", {
      trendCampaignId: campaignId ?? "all",
      statusCode: aggregateError.statusCode,
      message: aggregateError.message,
    });
  }
  const hasMeaningfulAggregateData = aggregated.some((entry) => {
    const delivered = entry.sent ?? 0;
    return (
      delivered > 0 ||
      (entry.opened ?? 0) > 0 ||
      (entry.clicked ?? 0) > 0 ||
      (entry.bounced ?? 0) > 0 ||
      (entry.complained ?? 0) > 0
    );
  });

  if (aggregated.length && hasMeaningfulAggregateData) {
    const aggregateDataset = mapAggregatesToDataset(aggregated, timeRange);
    return {
      dataset: aggregateDataset,
      placeholderText: null,
      apiError: aggregateError,
    };
  }

  const scopedSnapshots = await fetchCampaignScopedSnapshots(campaignId, {
    includeStats: false,
    includeTracking: true,
  });
  console.log(
    "snapshots fetched for SES performance trends:",
    scopedSnapshots
  );
  const records = scopedSnapshots.flatMap((snapshot) => snapshot.tracking ?? []);
  const days = getDaysFromRange(timeRange);
  const now = new Date();
  const rangeStart = new Date(now);
  rangeStart.setDate(rangeStart.getDate() - (days - 1));
  rangeStart.setHours(0, 0, 0, 0);

  const dayMap = buildEmptyDayMap(rangeStart, days);

  records.forEach((record: EnhancedMessageTrackingData) => {
    const deliveredTimestamp = getDeliveredTimestamp(record);
    if (!deliveredTimestamp || deliveredTimestamp < rangeStart.getTime()) {
      return;
    }

    const key = toDateKey(deliveredTimestamp);
    const entry = dayMap.get(key);
    if (!entry) {
      return;
    }

    entry.sent += 1;
    entry.delivered += 1;
    if (getOpenedTimestamp(record)) {
      entry.opened += 1;
    }
    if (getClickedTimestamp(record)) {
      entry.clicked += 1;
    }
    if (getBouncedTimestamp(record)) {
      entry.bounced += 1;
    }
    if (getComplaintTimestamp(record)) {
      entry.complained += 1;
    }
  });

  dayMap.forEach((entry) => {
    entry.deliveryRate =
      entry.sent > 0 ? roundToTwo((entry.delivered / entry.sent) * 100) : 0;
    entry.bounceRate =
      entry.sent > 0 ? roundToTwo((entry.bounced / entry.sent) * 100) : 0;
    entry.openRate =
      entry.delivered > 0
        ? roundToTwo((entry.opened / entry.delivered) * 100)
        : 0;
    entry.clickRate =
      entry.delivered > 0
        ? roundToTwo((entry.clicked / entry.delivered) * 100)
        : 0;
    entry.complaintRate =
      entry.delivered > 0
        ? roundToTwo((entry.complained / entry.delivered) * 100)
        : 0;
  });

  const fallbackDataset = Array.from(dayMap.values()).sort((a, b) =>
    a.date.localeCompare(b.date)
  );

  if (hasMeaningfulTrendData(fallbackDataset)) {
    return {
      dataset: fallbackDataset,
      placeholderText: null,
      apiError: aggregateError,
    };
  }

  return {
    dataset: [],
    placeholderText: NO_DATA_PLACEHOLDER,
    apiError: aggregateError,
  };
};

export const useSESPerformanceTrends = (
  timeRange: EngagementTimeRange = "30d",
  campaignId?: string
): UseSESPerformanceTrendsResult => {
  const debouncedCampaignId = useDebouncedValue(campaignId, DEBOUNCE_DELAY_MS);
  const queryResult = useQuery<SESPerformanceTrendResponse>({
    queryKey: [
      "ses-performance-trends",
      timeRange,
      debouncedCampaignId ?? "all",
    ],
    queryFn: () => buildTrendDataset(timeRange, debouncedCampaignId),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
  });
  const dataset = queryResult.data?.dataset ?? [];
  const placeholderText = queryResult.data?.placeholderText ?? null;
  const apiError = queryResult.data?.apiError ?? null;

  return Object.assign(queryResult, {
    dataset,
    placeholderText,
    apiError,
  });
};

const normalizeTrendApiError = (error: unknown): TrendDatasetApiError => {
  if (!error) {
    return { message: "Unknown error" };
  }

  const statusCode = extractStatusCode(error);
  const message = error instanceof Error ? error.message : "Failed to load trend data";

  return {
    statusCode,
    message,
  };
};

const extractStatusCode = (error: unknown): number | undefined => {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  const candidate = error as {
    statusCode?: number;
    status?: number;
    response?: { status?: number };
  };

  return (
    candidate.statusCode ??
    candidate.response?.status ??
    candidate.status
  );
};

const useDebouncedValue = <T>(value: T, delayMs: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [value, delayMs]);

  return debouncedValue;
};

export const __testables = {
  buildTrendDataset,
};
